import { useState, useEffect, useMemo, useRef, Fragment } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { MultiSelectFilter, parseMultiParam, serializeMultiParam } from "@/components/ui/multi-select-filter";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Search, ChevronLeft, ChevronRight, AlertCircle, ArrowUp, ArrowDown, ArrowUpDown, Mail, FileText, Pencil, Eye, Info, ExternalLink, CheckCircle2, MessageSquare, X as XIcon, MoreVertical, Link as LinkIcon, PawPrint, Filter, Heart, Star, Flag } from "lucide-react";
import { useFollowedAnimalIds } from "@/hooks/use-followed-animals";
import { useColumnFilters, ColumnFilter } from "@/components/ui/column-filter";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { EntityLinkerDialog } from "@/components/tasks/entity-linker-dialog";
import { CollapsibleFilterHeader } from "@/components/ui/collapsible-filter-header";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { InteractionDetailDrawer } from "@/components/interactions/InteractionDetailDrawer";
import { AnimalDetailDrawer } from "@/components/animals/AnimalDetailDrawer";
import { format, subDays, parseISO } from "date-fns";
import { useQuery } from "@tanstack/react-query";
import { useLocation, useSearch, Link } from "wouter";
import { useAuth } from "@/lib/auth-context";
import { getScoreBandColor } from "@/lib/score-utils";
import { Skeleton } from "@/components/ui/skeleton";
import { PageHeader } from "@/components/page-header";
import { useActiveLocations } from "@/hooks/use-active-locations";
import { SearchEmptyState } from "@/components/ui/search-empty-state";
import { AnimalCell } from "@/components/animals/animal-cell";
import { StatusDaysBadge } from "@/components/animals/StatusDaysBadge";
import { usePageTitle } from "@/hooks/use-page-title";
import { getKennelStatusBadgeClass, formatKennelStatus, STATUS_TRACK_OPTION_GROUPS, STATUS_TRACK_LABELS } from "@/lib/badge-styles";
import { useReportFreshness } from "@/hooks/use-report-freshness";
import { ReportFreshnessModal } from "@/components/report-freshness-modal";
import { ReportFreshnessText } from "@/components/report-freshness-text";
import { HighlightInteractionButton } from "@/components/interactions/HighlightInteractionButton";
import { FlagReason } from "@/lib/interaction-types";
import { HIGHLIGHTED_FILTER_PARAM } from "@/lib/constants";
import { cn } from "@/lib/utils";
import { highlightSubstringMatches } from "@/lib/highlight-keywords";
import { buildNoteExcerpt } from "@/lib/note-excerpt";
import { useZoneLabels } from "@/hooks/use-zone-labels";
import { InteractionHoverCard } from "@/components/interactions/InteractionHoverCard";

export interface Interaction {
  id: string;
  animalId: string;
  animalName: string | null;
  animalSpecies?: string | null;
  animalPhotoUrl?: string | null;
  kennelStatusNorm: string | null;
  substatusNorm?: string | null;
  kennelStatusSince?: string | null;
  locationCode: string;
  zoneCode: string;
  eventType: string;
  scoreCode: string | null;
  scoreBand: string | null;
  occurredAt: string;
  author: string | null;
  rawNote: string | null;
  sourceReportId: string | null;
  sourceEmailId: string | null;
  evaluationStatus: string | null;
  isFlagged: boolean;
  flagReasons: FlagReason[] | null;
}

interface InteractionsResponse {
  interactions: Interaction[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  filters: {
    startDate: string;
    endDate: string;
  };
}

interface ZonesResponse {
  ok: boolean;
  options: Array<{ value: string; label: string }>;
}


function InteractionDataWarning({ staleLocationCount, onOpenModal }: { staleLocationCount?: number; onOpenModal?: () => void }) {
  const hasStaleData = staleLocationCount && staleLocationCount > 0;
  
  if (!hasStaleData) {
    return (
      <div className="mb-4 flex items-center justify-between gap-4 flex-wrap" data-testid="text-interaction-data-info">
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Info className="h-4 w-4 shrink-0" />
          <span>Interaction data derived from uploaded reports</span>
        </div>
        <button
          type="button"
          className="flex items-center gap-1.5 text-sm text-emerald-700 dark:text-emerald-400 hover-elevate rounded-sm px-1"
          onClick={onOpenModal}
          data-testid="badge-report-freshness"
        >
          <CheckCircle2 className="h-4 w-4 shrink-0" />
          <span>All locations current</span>
        </button>
      </div>
    );
  }
  
  return (
    <Alert className="mb-4 border-blue-200 bg-blue-50 dark:border-blue-900 dark:bg-blue-950/30 [&>svg]:text-blue-600 dark:[&>svg]:text-blue-500">
      <Info className="h-4 w-4" />
      <AlertTitle className="text-blue-800 dark:text-blue-200" data-testid="text-interaction-data-info-stale">
        Interaction data derived from uploaded reports
        <span className="font-normal text-amber-700 dark:text-amber-400 ml-2">
          ({staleLocationCount} location{staleLocationCount !== 1 ? "s" : ""} with stale data)
        </span>
      </AlertTitle>
      <AlertDescription className="text-blue-700 dark:text-blue-300 flex flex-wrap items-center justify-between gap-2">
        <span>Upload new Interaction Summary reports to see the latest data.</span>
        <Button variant="outline" size="sm" onClick={onOpenModal} className="h-7 bg-blue-100 dark:bg-blue-900/50 border-blue-300 dark:border-blue-700 text-blue-800 dark:text-blue-200 ml-auto" data-testid="button-view-freshness-modal">
          View Details
        </Button>
      </AlertDescription>
    </Alert>
  );
}

const SCORE_CODES = ["1", "2", "3A", "3B", "4A", "4B", "5A", "5B"];
const EVENT_TYPES = [
  { value: "BEH_ALERT", label: "Alert" },
  { value: "OBSERVATION", label: "Observation" },
];



function formatEventType(eventType: string): string {
  return eventType === "BEH_ALERT" ? "Alert" : "Observation";
}

function ScoreCell({ scoreCode, scoreBand }: { scoreCode: string | null; scoreBand: string | null }) {
  if (!scoreCode) {
    return <span className="text-muted-foreground">–</span>;
  }
  
  return (
    <div className="flex items-center gap-1.5">
      <span className="font-medium">{scoreCode}</span>
      {scoreBand && (
        <Badge 
          variant="outline" 
          className={`text-xs px-1.5 py-0 h-5 ${getScoreBandColor(scoreBand)}`}
          data-testid={`badge-score-band-${scoreBand.toLowerCase()}`}
        >
          {scoreBand}
        </Badge>
      )}
    </div>
  );
}

function ProvenanceBadge({ sourceEmailId, sourceReportId }: { sourceEmailId: string | null; sourceReportId: string | null }) {
  if (sourceEmailId) {
    return (
      <Badge variant="outline" className="inline-flex items-center gap-1 text-xs w-fit text-blue-700 border-blue-300 dark:text-blue-400 dark:border-blue-700">
        <Mail className="h-3 w-3" />
        Email Alert
      </Badge>
    );
  }
  
  if (sourceReportId) {
    return (
      <Badge variant="outline" className="inline-flex items-center gap-1 text-xs w-fit">
        <FileText className="h-3 w-3" />
        Report
      </Badge>
    );
  }
  
  return (
    <Badge variant="outline" className="inline-flex items-center gap-1 text-xs w-fit">
      <Pencil className="h-3 w-3" />
      Manual
    </Badge>
  );
}

export default function Interactions() {
  usePageTitle("Interactions");
  const { resolveLabel } = useZoneLabels();
  const { user } = useAuth();
  const { data: followsData } = useFollowedAnimalIds();
  const followedIds = followsData?.animalIds ?? [];
  const [, setLocation] = useLocation();
  const searchString = useSearch();
  const searchParams = new URLSearchParams(searchString);
  
  const today = format(new Date(), "yyyy-MM-dd");
  const yesterday = format(subDays(new Date(), 1), "yyyy-MM-dd");
  const sevenDaysAgo = format(subDays(new Date(), 7), "yyyy-MM-dd");
  
  const hasUrlSearch = useRef(!!searchParams.get("search"));
  const [startDate, setStartDate] = useState(searchParams.get("startDate") || (hasUrlSearch.current ? "2020-01-01" : sevenDaysAgo));
  const [endDate, setEndDate] = useState(searchParams.get("endDate") || today);
  const hasUrlLocation = useRef(parseMultiParam(searchParams.get("locationCode")).length > 0);
  const hasUrlAnimalId = useRef(!!searchParams.get("animalId"));
  const initialAnimalId = useRef(searchParams.get("animalId") || "");
  const defaultLocationSeeded = useRef(false);
  const [locationCodes, setLocationCodes] = useState<string[]>(parseMultiParam(searchParams.get("locationCode")));
  const [zoneCode, setZoneCode] = useState(searchParams.get("zoneCode") || "all");
  const [kennelStatuses, setKennelStatuses] = useState<string[]>(parseMultiParam(searchParams.get("statusTrack")));
  const [scoreCodes, setScoreCodes] = useState<string[]>(parseMultiParam(searchParams.get("scoreCode")));
  const [scoreBand, setScoreBand] = useState(searchParams.get("scoreBand") || "all");
  const [eventTypes, setEventTypes] = useState<string[]>(parseMultiParam(searchParams.get("eventType")));
  const [evaluationStatuses, setEvaluationStatuses] = useState<string[]>(parseMultiParam(searchParams.get("evalStatus")));
  const [searchQuery, setSearchQuery] = useState(
    searchParams.get("search") || searchParams.get("author") || searchParams.get("animalId") || ""
  );
  const [highlighted, setHighlighted] = useState(searchParams.get(HIGHLIGHTED_FILTER_PARAM) === 'true');
  const [flagTypes, setFlagTypes] = useState<string[]>(parseMultiParam(searchParams.get("flagTypes")));
  const [filterCollapsed, setFilterCollapsed] = useState<boolean>(() => window.innerWidth < 640);
  const [page, setPage] = useState(parseInt(searchParams.get("page") || "1", 10));
  const [limit] = useState(25);
  const [sortBy, setSortBy] = useState(searchParams.get("sortBy") || "occurred_at");
  const [sortDir, setSortDir] = useState<"asc" | "desc">((searchParams.get("sortDir") as "asc" | "desc") || "desc");
  
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedInteraction, setSelectedInteraction] = useState<Interaction | null>(null);
  const [pendingInteractionId, setPendingInteractionId] = useState<string | null>(searchParams.get("interactionId"));

  const [entityLinkerOpen, setEntityLinkerOpen] = useState(false);
  const [linkerInteraction, setLinkerInteraction] = useState<Interaction | null>(null);

  const [animalPreviewOpen, setAnimalPreviewOpen] = useState(false);
  const [previewAnimalId, setPreviewAnimalId] = useState<string | null>(null);
  const [hoveredInteractionId, setHoveredInteractionId] = useState<string | null>(null);
  const hoverTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const openHoverCard = (id: string) => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    if (hoveredInteractionId === id) return;
    hoverTimerRef.current = setTimeout(() => setHoveredInteractionId(id), 500);
  };
  const closeHoverCard = () => {
    if (hoverTimerRef.current) clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = setTimeout(() => setHoveredInteractionId(null), 150);
  };

  const handleOpenDrawer = (interaction: Interaction) => {
    setSelectedInteraction(interaction);
    setDrawerOpen(true);
  };

  const handleOpenLinker = (interaction: Interaction) => {
    setLinkerInteraction(interaction);
    setEntityLinkerOpen(true);
  };

  // Phase 1c: Seed default location once user resolves (if no URL location was present at mount)
  // B1: also skip seeding when animalId was in the URL — the link carries full-history intent
  // S12-A: also skip seeding when a search param was in the URL — search-driven loads should be unscoped by location
  useEffect(() => {
    if (
      !defaultLocationSeeded.current &&
      !hasUrlLocation.current &&
      !hasUrlAnimalId.current &&
      !hasUrlSearch.current &&
      user?.defaultLocationCode
    ) {
      setLocationCodes([user.defaultLocationCode]);
      defaultLocationSeeded.current = true;
    }
  }, [user?.defaultLocationCode]);

  // Sync URL params back to state when URL changes (e.g., from author/animal link click)
  useEffect(() => {
    const params = new URLSearchParams(searchString);
    // Unified search: accept ?search=, ?author=, or ?animalId= for backward compat
    const urlSearch = params.get("search") || params.get("author") || params.get("animalId") || "";
    const urlStartDate = params.get("startDate");
    const urlEndDate = params.get("endDate");
    const urlScoreBand = params.get("scoreBand") || "all";
    const urlInteractionId = params.get("interactionId");
    
    // Only update if the URL param differs from current state
    if (urlSearch !== searchQuery) {
      setSearchQuery(urlSearch);
      setPage(1);
    }
    if (urlStartDate && urlStartDate !== startDate) {
      setStartDate(urlStartDate);
    }
    if (urlEndDate && urlEndDate !== endDate) {
      setEndDate(urlEndDate);
    }
    if (urlScoreBand !== scoreBand) {
      setScoreBand(urlScoreBand);
    }
    // Sync interactionId for auto-open drawer navigation
    if (urlInteractionId && urlInteractionId !== pendingInteractionId) {
      setPendingInteractionId(urlInteractionId);
    }
    // S12.4 (revised): sentinel from note-text link-out — reset ALL filters except sort order
    if (params.get("resetFilters") === "1") {
      setLocationCodes([]);
      setZoneCode("all");
      setKennelStatuses([]);
      setScoreCodes([]);
      setScoreBand("all");
      setEventTypes([]);
      setEvaluationStatuses([]);
      setFlagTypes([]);
      setHighlighted(false);
      setPage(1);
    }
  }, [searchString]);

  const { locations: activeLocations, isLoading: isLocationsLoading, error: locationsError } = useActiveLocations();

  const { data: zonesData, isLoading: isZonesLoading } = useQuery<ZonesResponse>({
    queryKey: ["/api/meta/zones"],
  });

  const zones = zonesData?.options || [];

  // Freshness data for warning banner and modal
  const [freshnessModalOpen, setFreshnessModalOpen] = useState(false);
  const { interaction: interactionFreshness } = useReportFreshness();

  const staleLocationCount = interactionFreshness.staleLocations.length;

  const buildQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("startDate", startDate);
    params.set("endDate", endDate);
    const locSer = serializeMultiParam(locationCodes); if (locSer) params.set("locationCode", locSer);
    if (zoneCode !== "all") params.set("zoneCode", zoneCode);
    const ksSer = serializeMultiParam(kennelStatuses); if (ksSer) params.set("statusTrack", ksSer);
    const scSer = serializeMultiParam(scoreCodes); if (scSer) params.set("scoreCode", scSer);
    if (scoreBand !== "all") params.set("scoreBand", scoreBand);
    const etSer = serializeMultiParam(eventTypes); if (etSer) params.set("eventType", etSer);
    const evSer = serializeMultiParam(evaluationStatuses); if (evSer) params.set("evalStatus", evSer);
    if (searchQuery.trim()) {
      if (initialAnimalId.current && searchQuery.trim() === initialAnimalId.current) {
        params.set("animalId", searchQuery.trim());
      } else {
        params.set("search", searchQuery.trim());
      }
    }
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir);
    params.set("page", String(page));
    params.set("limit", String(limit));
    if (highlighted) params.set(HIGHLIGHTED_FILTER_PARAM, "true");
    const ftSer = serializeMultiParam(flagTypes); if (ftSer) params.set("flagTypes", ftSer);
    return params.toString();
  }, [startDate, endDate, locationCodes, zoneCode, kennelStatuses, scoreCodes, scoreBand, eventTypes, evaluationStatuses, searchQuery, page, limit, sortBy, sortDir, highlighted, flagTypes]);

  const { data, isLoading, error } = useQuery<InteractionsResponse>({
    queryKey: ["/api/interactions", buildQueryString],
    queryFn: async () => {
      const response = await fetch(`/api/interactions?${buildQueryString}`, {
        credentials: "include",
      });
      if (!response.ok) {
        throw new Error(`Failed to fetch interactions: ${response.statusText}`);
      }
      return response.json();
    },
  });

  useEffect(() => {
    const params = new URLSearchParams();
    // Include dates when: startDate is non-default, endDate is non-default, OR scoreBand is filtered
    // The scoreBand case ensures "View All" from dashboard preserves the date range context
    const isNonDefaultStart = startDate !== sevenDaysAgo;
    const hasActiveScoreBandFilter = scoreBand !== "all";
    if (isNonDefaultStart || hasActiveScoreBandFilter) {
      params.set("startDate", startDate);
      params.set("endDate", endDate);
    } else if (endDate !== today) {
      params.set("endDate", endDate);
    }
    const locSer = serializeMultiParam(locationCodes); if (locSer) params.set("locationCode", locSer);
    if (zoneCode !== "all") params.set("zoneCode", zoneCode);
    const ksSer = serializeMultiParam(kennelStatuses); if (ksSer) params.set("statusTrack", ksSer);
    const scSer = serializeMultiParam(scoreCodes); if (scSer) params.set("scoreCode", scSer);
    if (scoreBand !== "all") params.set("scoreBand", scoreBand);
    const etSer = serializeMultiParam(eventTypes); if (etSer) params.set("eventType", etSer);
    const evSer = serializeMultiParam(evaluationStatuses); if (evSer) params.set("evalStatus", evSer);
    if (searchQuery.trim()) {
      if (initialAnimalId.current && searchQuery.trim() === initialAnimalId.current) {
        params.set("animalId", searchQuery.trim());
      } else {
        params.set("search", searchQuery.trim());
      }
    }
    if (page > 1) params.set("page", String(page));
    if (sortBy !== "occurred_at") params.set("sortBy", sortBy);
    if (sortDir !== "desc") params.set("sortDir", sortDir);
    if (highlighted) params.set(HIGHLIGHTED_FILTER_PARAM, "true");
    const ftUrlSer = serializeMultiParam(flagTypes); if (ftUrlSer) params.set("flagTypes", ftUrlSer);
    // Preserve interactionId when drawer is open for deep-linking
    if (drawerOpen && selectedInteraction?.id) {
      params.set("interactionId", selectedInteraction.id);
    }
    
    const queryString = params.toString();
    const newPath = queryString ? `/interactions?${queryString}` : "/interactions";
    
    if (window.location.pathname + window.location.search !== newPath) {
      window.history.replaceState(null, "", newPath);
    }
  }, [startDate, endDate, locationCodes, zoneCode, kennelStatuses, scoreCodes, scoreBand, eventTypes, evaluationStatuses, searchQuery, page, sevenDaysAgo, today, sortBy, sortDir, drawerOpen, selectedInteraction?.id, highlighted, flagTypes]);

  useEffect(() => {
    if (pendingInteractionId && data?.interactions) {
      const interactionInPage = data.interactions.find(i => i.id === pendingInteractionId);
      if (interactionInPage) {
        setSelectedInteraction(interactionInPage);
        setDrawerOpen(true);
        setPendingInteractionId(null);
      } else if (!isLoading) {
        fetch(`/api/interactions/${pendingInteractionId}`, { credentials: "include" })
          .then(res => res.ok ? res.json() : null)
          .then((interactionData: Interaction | null) => {
            if (interactionData) {
              setSelectedInteraction(interactionData);
              setDrawerOpen(true);
            }
            setPendingInteractionId(null);
          })
          .catch(() => setPendingInteractionId(null));
      }
    }
  }, [pendingInteractionId, data?.interactions, isLoading]);

  const handleDatePreset = (days: number) => {
    const newEnd = format(new Date(), "yyyy-MM-dd");
    const newStart = format(subDays(new Date(), days), "yyyy-MM-dd");
    setStartDate(newStart);
    setEndDate(newEnd);
    setPage(1);
  };

  const handleSingleDayPreset = (date: string) => {
    setStartDate(date);
    setEndDate(date);
    setPage(1);
  };

  const handleAllTimePreset = () => {
    setStartDate("2020-01-01");
    setEndDate(format(new Date(), "yyyy-MM-dd"));
    setPage(1);
  };

  const isAllTimeSelected = startDate === "2020-01-01" && endDate === today;

  const handleSort = (column: string) => {
    if (sortBy === column) {
      setSortDir(sortDir === "asc" ? "desc" : "asc");
    } else {
      setSortBy(column);
      setSortDir("asc");
    }
    setPage(1);
  };

  const resetKey = useMemo(
    () => [startDate, endDate, locationCodes.join(","), zoneCode, kennelStatuses.join(","), scoreCodes.join(","), scoreBand, eventTypes.join(","), evaluationStatuses.join(","), searchQuery, flagTypes.join(","), String(highlighted)].join("|"),
    [startDate, endDate, locationCodes, zoneCode, kennelStatuses, scoreCodes, scoreBand, eventTypes, evaluationStatuses, searchQuery, flagTypes, highlighted]
  );

  const columnFilters = useColumnFilters(resetKey);

  const paginationTotal = data?.pagination?.total ?? Infinity;
  const canColumnFilter = paginationTotal <= 500;

  const allQueryString = useMemo(() => {
    const params = new URLSearchParams();
    params.set("startDate", startDate);
    params.set("endDate", endDate);
    const locSerA = serializeMultiParam(locationCodes); if (locSerA) params.set("locationCode", locSerA);
    if (zoneCode !== "all") params.set("zoneCode", zoneCode);
    const ksSerA = serializeMultiParam(kennelStatuses); if (ksSerA) params.set("statusTrack", ksSerA);
    const scSerA = serializeMultiParam(scoreCodes); if (scSerA) params.set("scoreCode", scSerA);
    if (scoreBand !== "all") params.set("scoreBand", scoreBand);
    const etSerA = serializeMultiParam(eventTypes); if (etSerA) params.set("eventType", etSerA);
    const evSerA = serializeMultiParam(evaluationStatuses); if (evSerA) params.set("evalStatus", evSerA);
    if (searchQuery.trim()) {
      if (initialAnimalId.current && searchQuery.trim() === initialAnimalId.current) {
        params.set("animalId", searchQuery.trim());
      } else {
        params.set("search", searchQuery.trim());
      }
    }
    params.set("sortBy", sortBy);
    params.set("sortDir", sortDir);
    params.set("page", "1");
    params.set("limit", "500");
    if (highlighted) params.set(HIGHLIGHTED_FILTER_PARAM, "true");
    const ftSerA = serializeMultiParam(flagTypes); if (ftSerA) params.set("flagTypes", ftSerA);
    return params.toString();
  }, [startDate, endDate, locationCodes, zoneCode, kennelStatuses, scoreCodes, scoreBand, eventTypes, evaluationStatuses, searchQuery, sortBy, sortDir, highlighted, flagTypes]);

  const { data: allData } = useQuery<InteractionsResponse>({
    queryKey: ["/api/interactions", "all-for-column-filter", allQueryString],
    queryFn: async () => {
      const response = await fetch(`/api/interactions?${allQueryString}`, { credentials: "include" });
      if (!response.ok) throw new Error("Failed to fetch all interactions");
      return response.json();
    },
    enabled: canColumnFilter,
    staleTime: 30_000,
  });

  const isColumnFilterMode = canColumnFilter && allData != null;

  const getSourceValue = (i: Interaction) =>
    i.sourceEmailId ? "Email Alert" : i.sourceReportId ? "Report" : "Manual";

  const TALL_FILTER_COLUMNS = new Set(["location", "name", "dateReported"]);

  const getDateLabel = (occurredAt: string) => {
    const dateStr = occurredAt;
    const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()}, ${date.getUTCFullYear()}`;
  };

  const columnFilterValues = useMemo(() => {
    if (!isColumnFilterMode || !allData?.interactions) return {} as Record<string, string[]>;
    const all = allData.interactions;
    return {
      dateReported: [...new Set(all.map(i => getDateLabel(i.occurredAt)))],
      name: [...new Set(all.map(i => i.animalName || ""))],
      location: [...new Set(all.map(i => i.locationCode || ""))],
      status: [...new Set(all.map(i => formatKennelStatus(i.kennelStatusNorm, i.substatusNorm)))],
      type: [...new Set(all.map(i => formatEventType(i.eventType)))],
      scoreBand: [...new Set(all.map(i => i.scoreBand || ""))],
      source: [...new Set(all.map(i => getSourceValue(i)))],
    };
  }, [isColumnFilterMode, allData?.interactions]);

  const columnFilterGetters: Record<string, (item: Interaction) => string> = useMemo(() => ({
    dateReported: (i: Interaction) => getDateLabel(i.occurredAt),
    name: (i: Interaction) => i.animalName || "",
    location: (i: Interaction) => i.locationCode || "",
    status: (i: Interaction) => formatKennelStatus(i.kennelStatusNorm, i.substatusNorm),
    type: (i: Interaction) => formatEventType(i.eventType),
    scoreBand: (i: Interaction) => i.scoreBand || "",
    source: (i: Interaction) => getSourceValue(i),
  }), []);

  const columnFilteredInteractions = useMemo(() => {
    if (!isColumnFilterMode || !allData?.interactions) return [];
    return columnFilters.applyFilters(allData.interactions, columnFilterGetters);
  }, [isColumnFilterMode, allData?.interactions, columnFilters, columnFilterGetters]);

  const [columnFilterPage, setColumnFilterPage] = useState(1);
  useEffect(() => { setColumnFilterPage(1); }, [columnFilters.filters]);

  const displayInteractions = useMemo(() => {
    if (isColumnFilterMode && columnFilters.hasAnyActive()) {
      const start = (columnFilterPage - 1) * limit;
      return columnFilteredInteractions.slice(start, start + limit);
    }
    if (isColumnFilterMode) {
      const start = (columnFilterPage - 1) * limit;
      return (allData?.interactions || []).slice(start, start + limit);
    }
    return data?.interactions || [];
  }, [isColumnFilterMode, columnFilters, columnFilterPage, limit, columnFilteredInteractions, allData?.interactions, data?.interactions]);

  const displayPagination = useMemo(() => {
    if (isColumnFilterMode) {
      const total = columnFilters.hasAnyActive() ? columnFilteredInteractions.length : (allData?.interactions?.length || 0);
      const totalPages = Math.max(1, Math.ceil(total / limit));
      return { page: columnFilterPage, limit, total, totalPages };
    }
    return data?.pagination || { page: 1, limit: 25, total: 0, totalPages: 0 };
  }, [isColumnFilterMode, columnFilters, columnFilteredInteractions.length, allData?.interactions?.length, columnFilterPage, limit, data?.pagination]);

  const renderSortIcon = (column: string) => {
    if (sortBy === column) {
      return sortDir === "asc" ? <ArrowUp className="h-3 w-3" /> : <ArrowDown className="h-3 w-3" />;
    }
    return <ArrowUpDown className="h-3 w-3 opacity-50" />;
  };

  const renderColumnFilter = (columnKey: string) => {
    if (!isColumnFilterMode) return null;
    return (
      <ColumnFilter
        columnKey={columnKey}
        values={columnFilterValues[columnKey] || []}
        selectedValues={columnFilters.filters[columnKey]}
        onSelectionChange={columnFilters.setFilter}
        onClear={columnFilters.clearFilter}
        isActive={columnFilters.isActive(columnKey)}
        maxHeight={TALL_FILTER_COLUMNS.has(columnKey) ? "20rem" : "13rem"}
      />
    );
  };

  const renderSortableHeader = (column: string, label: string, filterColumn?: string, className?: string) => (
    <th className={`${className || ""} sticky top-0 z-30 bg-card shadow-[0_1px_0_0_hsl(var(--border))] px-3 py-2 text-left text-sm font-medium text-muted-foreground whitespace-nowrap`}>
      <div className="flex items-center gap-1">
        <button
          className="flex items-center gap-1 hover:text-foreground transition-colors cursor-pointer"
          onClick={() => handleSort(column)}
          data-testid={`sort-${column}`}
        >
          {label}
          {renderSortIcon(column)}
        </button>
        {filterColumn && renderColumnFilter(filterColumn)}
      </div>
    </th>
  );

  const handleClearFilters = () => {
    setStartDate(sevenDaysAgo);
    setEndDate(today);
    setLocationCodes([]);
    setZoneCode("all");
    setKennelStatuses([]);
    setScoreCodes([]);
    setScoreBand("all");
    setEventTypes([]);
    setEvaluationStatuses([]);
    setSearchQuery("");
    setFlagTypes([]);
    setPage(1);
  };

  const hasActiveFilters = 
    startDate !== sevenDaysAgo ||
    endDate !== today ||
    locationCodes.length > 0 ||
    zoneCode !== "all" ||
    kennelStatuses.length > 0 ||
    scoreCodes.length > 0 ||
    scoreBand !== "all" ||
    eventTypes.length > 0 ||
    evaluationStatuses.length > 0 ||
    flagTypes.length > 0 ||
    searchQuery.trim() !== "";

  const isDefaultLocationActive =
    locationCodes.length === 1 &&
    locationCodes[0] === user?.defaultLocationCode;

  const description = useMemo(() => {
    const parts: string[] = [];
    if (searchQuery.trim()) parts.push(`matching "${searchQuery.trim()}"`);
    if (locationCodes.length === 1) parts.push(`at ${locationCodes[0]}`);
    else if (locationCodes.length > 1) parts.push(`at ${locationCodes.length} locations`);
    if (eventTypes.length === 1) parts.push(`type: ${eventTypes[0]}`);
    else if (eventTypes.length > 1) parts.push(`${eventTypes.length} types`);
    if (scoreCodes.length === 1) parts.push(`score: ${scoreCodes[0]}`);
    else if (scoreCodes.length > 1) parts.push(`scores: ${scoreCodes.join(", ")}`);
    if (kennelStatuses.length === 1) parts.push(`status: ${STATUS_TRACK_LABELS[kennelStatuses[0]] ?? kennelStatuses[0]}`);
    else if (kennelStatuses.length > 1) parts.push(`${kennelStatuses.length} statuses`);
    if (evaluationStatuses.length === 1) parts.push(`eval: ${evaluationStatuses[0]}`);
    else if (evaluationStatuses.length > 1) parts.push(`${evaluationStatuses.length} eval statuses`);
    if (flagTypes.length === 1) {
      const FLAG_TYPE_LABELS: Record<string, string> = { long_note: "Long note", keyword: "Keyword match" };
      parts.push(`flag: ${FLAG_TYPE_LABELS[flagTypes[0]] ?? flagTypes[0]}`);
    } else if (flagTypes.length > 1) {
      parts.push(`${flagTypes.length} flag types`);
    }
    if (highlighted) parts.push("highlighted only");
    const text = parts.length > 0
      ? `Interactions ${parts.join(", ")}`
      : "All interactions in selected date range";
    if (isDefaultLocationActive) {
      return (
        <>
          <Star className="inline-block h-3 w-3 text-amber-400 fill-amber-400 mr-0.5" />
          {" "}{text}
        </>
      );
    }
    return text;
  }, [searchQuery, locationCodes, eventTypes, scoreCodes, kennelStatuses, evaluationStatuses, flagTypes, highlighted, isDefaultLocationActive]);

  const handlePageChange = (newPage: number) => {
    if (isColumnFilterMode) {
      setColumnFilterPage(newPage);
    } else {
      setPage(newPage);
    }
  };

  return (
    <div className="flex-1 px-4 sm:px-6 pb-6 pt-4">
      <PageHeader
        breadcrumb={[
          { label: "Reporting" },
          { label: "Interactions" },
        ]}
        title={
          <span className="flex items-center gap-2" data-testid="heading-interactions">
            <MessageSquare className="w-6 h-6 text-primary" />
            Behavior Interactions
          </span>
        }
        actions={<ReportFreshnessText reportTypes={["allPets", "interaction"]} testIdPrefix="interactions" />}
      />

      <div className="sticky top-0 z-40 -mx-4 sm:-mx-6 px-4 sm:px-6 pt-3 pb-4 border-b bg-muted/80 backdrop-blur supports-[backdrop-filter]:bg-muted/60 mb-4">
        <Card>
          <CardContent className="p-5">
            <CollapsibleFilterHeader
              collapsed={filterCollapsed}
              onToggle={() => setFilterCollapsed((c) => !c)}
              description={description}
              hasActiveFilters={hasActiveFilters}
              clearLabel={isDefaultLocationActive ? "Clear default" : "Clear"}
              onClearFilters={handleClearFilters}
            >
            
              <div className="flex flex-col gap-4">
                {/* Date row */}
                <div className="flex flex-wrap items-center gap-2">
                  <div className="flex items-center gap-2">
                    <Input
                      type="date"
                      value={startDate}
                      onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                      className="w-[140px]"
                      data-testid="input-start-date"
                    />
                    <span className="text-muted-foreground">to</span>
                    <Input
                      type="date"
                      value={endDate}
                      onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                      className="w-[140px]"
                      data-testid="input-end-date"
                    />
                  </div>
                  <div className="flex items-center gap-1">
                    <Button variant={startDate === today && endDate === today ? "default" : "outline"} size="sm" onClick={() => handleSingleDayPreset(today)} data-testid="button-preset-today">Today</Button>
                    <Button variant={startDate === yesterday && endDate === yesterday ? "default" : "outline"} size="sm" onClick={() => handleSingleDayPreset(yesterday)} data-testid="button-preset-yesterday">Yesterday</Button>
                    <Button variant={startDate === sevenDaysAgo && endDate === today ? "default" : "outline"} size="sm" onClick={() => handleDatePreset(7)} data-testid="button-preset-7d">7d</Button>
                    <Button variant={startDate === format(subDays(new Date(), 14), "yyyy-MM-dd") && endDate === today ? "default" : "outline"} size="sm" onClick={() => handleDatePreset(14)} data-testid="button-preset-14d">14d</Button>
                    <Button variant={startDate === format(subDays(new Date(), 30), "yyyy-MM-dd") && endDate === today ? "default" : "outline"} size="sm" onClick={() => handleDatePreset(30)} data-testid="button-preset-30d">30d</Button>
                    <Button variant={startDate === format(subDays(new Date(), 90), "yyyy-MM-dd") && endDate === today ? "default" : "outline"} size="sm" onClick={() => handleDatePreset(90)} data-testid="button-preset-90d">90d</Button>
                    <Button variant={isAllTimeSelected ? "default" : "outline"} size="sm" onClick={handleAllTimePreset} data-testid="button-preset-all-time">All time</Button>
                  </div>
                  {/* Unified search — Row 1 (B2) */}
                  <div className="flex-1 min-w-[200px] relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
                    <Input
                      placeholder="Search by name, animal ID, author, or note text..."
                      value={searchQuery}
                      onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                      className="pl-9"
                      data-testid="input-search"
                    />
                  </div>
                </div>

                {/* Dropdowns row */}
                <div className="flex flex-wrap items-center gap-2">
                  {isLocationsLoading ? (
                    <Skeleton className="h-9 w-[150px]" />
                  ) : (
                    <MultiSelectFilter
                      label="Locations"
                      options={activeLocations.map(loc => ({ value: loc.code, label: loc.label }))}
                      selected={locationCodes}
                      onChange={(vals) => { setLocationCodes(vals); setZoneCode("all"); setPage(1); }}
                      className="w-[150px]"
                      data-testid="select-location"
                    />
                  )}

                  <MultiSelectFilter
                    label="Kennels"
                    options={zones}
                    selected={zoneCode === "all" ? [] : [zoneCode]}
                    onChange={(vals) => { setZoneCode(vals.length > 0 ? vals[vals.length - 1] : "all"); setPage(1); }}
                    className="w-[160px]"
                    data-testid="select-zone"
                  />

                  <MultiSelectFilter
                    label="Statuses"
                    optionGroups={STATUS_TRACK_OPTION_GROUPS}
                    selected={kennelStatuses}
                    onChange={(vals) => { setKennelStatuses(vals); setPage(1); }}
                    className="w-[140px]"
                    data-testid="select-kennel-status"
                  />

                  <MultiSelectFilter
                    label="Scores"
                    options={SCORE_CODES.map(code => ({ value: code, label: code }))}
                    selected={scoreCodes}
                    onChange={(vals) => { setScoreCodes(vals); setPage(1); }}
                    className="w-[130px]"
                    data-testid="select-score-code"
                  />

                  <MultiSelectFilter
                    label="Types"
                    options={EVENT_TYPES}
                    selected={eventTypes}
                    onChange={(vals) => { setEventTypes(vals); setPage(1); }}
                    className="w-[140px]"
                    data-testid="select-event-type"
                  />

                  <MultiSelectFilter
                    label="Evaluation"
                    options={[
                      { value: "needs_review", label: "Needs Review" },
                      { value: "needs_recs", label: "Needs Recs" },
                      { value: "complete", label: "Complete" },
                      { value: "none", label: "No Evaluation" },
                      { value: "other", label: "Other" },
                    ]}
                    selected={evaluationStatuses}
                    onChange={(vals) => { setEvaluationStatuses(vals); setPage(1); }}
                    className="w-[160px]"
                    data-testid="select-evaluation-status"
                  />

                  <MultiSelectFilter
                    label="Flag Type"
                    options={[
                      { value: "long_note", label: "Long note" },
                      { value: "keyword",   label: "Keyword match" },
                    ]}
                    selected={flagTypes}
                    onChange={(vals) => { setFlagTypes(vals); setPage(1); }}
                    className="w-[150px]"
                    data-testid="select-flag-type"
                  />

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => { setHighlighted(!highlighted); setPage(1); }}
                    className={cn("gap-1.5", highlighted && "bg-amber-50 border-amber-300 text-amber-700 dark:bg-amber-950 dark:border-amber-700 dark:text-amber-400")}
                    data-testid="button-filter-highlighted"
                  >
                    <Flag className={cn("h-3.5 w-3.5", highlighted && "fill-current")} />
                    Highlighted
                  </Button>
                </div>
              </div>
            </CollapsibleFilterHeader>
          </CardContent>
        </Card>
      </div>

      {isColumnFilterMode && columnFilters.hasAnyActive() && (
        <div className="flex items-center gap-2 mb-2 text-sm text-muted-foreground" data-testid="text-column-filter-info">
          <Info className="h-3.5 w-3.5 shrink-0" />
          <span>Column filters active — showing {columnFilteredInteractions.length} of {allData?.interactions?.length || 0} interactions</span>
          <Button
            variant="ghost"
            size="sm"
            onClick={columnFilters.clearAll}
            className="h-6 text-xs px-2 ml-auto"
            data-testid="button-clear-all-column-filters"
          >
            <XIcon className="h-3 w-3 mr-1" />
            Clear column filters
          </Button>
        </div>
      )}

      {!canColumnFilter && paginationTotal > 500 && (
        <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground" data-testid="text-column-filter-hint">
          <Filter className="h-3 w-3 shrink-0" />
          <span>Narrow your filters to enable in-table column filtering (showing {paginationTotal} results, max 500)</span>
        </div>
      )}

      {searchQuery.trim() && (
        <div className="flex items-center gap-2 mb-2 text-xs text-muted-foreground" data-testid="text-search-scope-note">
          <Info className="h-3 w-3 shrink-0" />
          <span>Results include both current and previously outcomed animals.</span>
        </div>
      )}

      <InteractionDataWarning
        staleLocationCount={staleLocationCount}
        onOpenModal={() => setFreshnessModalOpen(true)}
      />

      <ReportFreshnessModal
        open={freshnessModalOpen}
        onOpenChange={setFreshnessModalOpen}
        staleLocations={interactionFreshness.staleLocations}
        locationsWithGaps={interactionFreshness.locationsWithGaps}
        reportTypes={["interaction"]}
        allLocations={interactionFreshness.locations}
      />

      {error ? (
        <div className="flex items-center justify-center gap-2 p-8 text-destructive">
          <AlertCircle className="h-5 w-5" />
          <span>Failed to load interactions. Please try again.</span>
        </div>
      ) : (
        <>
          <div className="rounded-md border border-border bg-card w-full max-w-full overflow-auto" style={{ maxHeight: "calc(100vh - 16rem)" }}>
            <table className="w-full caption-bottom text-sm min-w-[1050px] table-zebra">
                  <thead>
                    <tr>
                      {renderSortableHeader("occurred_at", "Date Reported", "dateReported", "w-[160px]")}
                      {renderSortableHeader("animal_name", "Animal", "name", "w-[150px]")}
                      {renderSortableHeader("kennel_status", "Status", "status", "w-[100px]")}
                      {renderSortableHeader("location_code", "Location", "location", "w-[100px]")}
                      {renderSortableHeader("zone_code", "Kennel", undefined, "w-[120px]")}
                      {renderSortableHeader("event_type", "Type", "type", "w-[100px]")}
                      {renderSortableHeader("score_code", "Score", "scoreBand", "w-[120px]")}
                      {renderSortableHeader("author", "Author", undefined, "w-[100px]")}
                      {renderSortableHeader("source", "Source", "source", "w-[110px]")}
                      <th className="sticky top-0 z-30 bg-card shadow-[0_1px_0_0_hsl(var(--border))] px-3 py-2 text-right text-sm font-medium text-muted-foreground w-[80px]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i} className="border-b">
                          <td colSpan={10} className="p-3">
                            <Skeleton className="h-10 w-full" />
                          </td>
                        </tr>
                      ))
                    ) : displayInteractions.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="py-8">
                          {isColumnFilterMode && columnFilters.hasAnyActive() ? (
                            <div className="flex flex-col items-center justify-center gap-3 text-muted-foreground" data-testid="text-column-filter-empty">
                              <Filter className="h-8 w-8 opacity-40" />
                              <p className="text-sm">No interactions match the active column filters</p>
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={columnFilters.clearAll}
                                data-testid="button-clear-column-filters-empty"
                              >
                                Clear column filters
                              </Button>
                            </div>
                          ) : hasActiveFilters ? (
                            <SearchEmptyState isSearchActive={!!searchQuery.trim()} />
                          ) : (
                            <div className="text-center text-muted-foreground">
                              No behavior interactions have been recorded yet.
                            </div>
                          )}
                        </td>
                      </tr>
                    ) : (
                      displayInteractions.map((interaction) => {
                        const excerpt = searchQuery.trim()
                          ? buildNoteExcerpt(interaction.rawNote, searchQuery.trim())
                          : null;
                        return (
                          <Fragment key={interaction.id}>
                          <tr
                            className="border-b transition-colors hover:bg-muted/50 cursor-pointer"
                            onClick={() => handleOpenDrawer(interaction)}
                            data-testid={`row-interaction-${interaction.id}`}
                          >
                          <td
                            className="p-3 align-middle"
                            data-testid={`text-date-${interaction.id}`}
                            onMouseEnter={() => openHoverCard(interaction.id)}
                            onMouseLeave={closeHoverCard}
                          >
                            {(() => {
                              const dateStr = interaction.occurredAt;
                              const date = new Date(dateStr.endsWith('Z') ? dateStr : dateStr + 'Z');
                              const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                              const month = months[date.getUTCMonth()];
                              const day = date.getUTCDate();
                              const year = date.getUTCFullYear();
                              const hours = date.getUTCHours();
                              const mins = date.getUTCMinutes().toString().padStart(2, '0');
                              const ampm = hours >= 12 ? 'PM' : 'AM';
                              const hour12 = hours % 12 || 12;
                              return `${month} ${day}, ${year} ${hour12}:${mins} ${ampm}`;
                            })()}
                          </td>
                          <td
                            className="p-3 align-middle"
                            data-testid={`text-name-${interaction.id}`}
                            onMouseEnter={() => openHoverCard(interaction.id)}
                            onMouseLeave={closeHoverCard}
                          >
                            <InteractionHoverCard
                              interaction={interaction}
                              zoneLabel={resolveLabel(interaction.zoneCode)}
                              searchQuery={searchQuery}
                              onOpenDrawer={() => handleOpenDrawer(interaction)}
                              onViewAnimal={() => {
                                setPreviewAnimalId(interaction.animalId);
                                setAnimalPreviewOpen(true);
                              }}
                              open={hoveredInteractionId === interaction.id}
                              onOpenChange={(o) => { if (!o) closeHoverCard(); }}
                            >
                              <AnimalCell
                                animalId={interaction.animalId}
                                photoUrl={interaction.animalPhotoUrl ?? null}
                                name={interaction.animalName ?? null}
                                species={interaction.animalSpecies ?? null}
                                speciesTestId={`badge-species-${interaction.id}`}
                                inlineBadges={
                                  <>
                                    {followedIds.includes(interaction.animalId) && <Heart className="h-3 w-3 fill-rose-500 text-rose-500 shrink-0" data-testid={`heart-followed-${interaction.animalId}`} />}
                                    {interaction.flagReasons?.some((r) => r.type === "highlighted") && (
                                      <Flag className="h-3 w-3 fill-amber-500 text-amber-500 shrink-0" data-testid={`flag-highlighted-${interaction.id}`} />
                                    )}
                                  </>
                                }
                              />
                            </InteractionHoverCard>
                          </td>
                          <td className="p-3 align-middle" data-testid={`cell-status-${interaction.id}`}>
                            <StatusDaysBadge
                              status={interaction.kennelStatusNorm}
                              substatus={interaction.substatusNorm}
                              statusSince={interaction.kennelStatusSince}
                              data-testid={`badge-kennel-status-${interaction.id}`}
                            />
                          </td>
                          <td className="p-3 align-middle">
                            <Badge variant="outline" data-testid={`badge-location-${interaction.id}`}>
                              {interaction.locationCode}
                            </Badge>
                          </td>
                          <td className="p-3 align-middle text-sm" data-testid={`text-zone-${interaction.id}`}>
                            {resolveLabel(interaction.zoneCode)}
                          </td>
                          <td className="p-3 align-middle" data-testid={`text-type-${interaction.id}`}>
                            {formatEventType(interaction.eventType)}
                          </td>
                          <td className="p-3 align-middle" data-testid={`cell-score-${interaction.id}`}>
                            <ScoreCell 
                              scoreCode={interaction.scoreCode} 
                              scoreBand={interaction.scoreBand} 
                            />
                          </td>
                          <td className="p-3 align-middle text-sm" data-testid={`text-author-${interaction.id}`}>
                            {interaction.author || "–"}
                          </td>
                          <td className="p-3 align-middle" data-testid={`cell-source-${interaction.id}`}>
                            <ProvenanceBadge 
                              sourceEmailId={interaction.sourceEmailId} 
                              sourceReportId={interaction.sourceReportId} 
                            />
                          </td>
                          <td className="p-3 align-middle text-right" onClick={(e) => e.stopPropagation()} data-testid={`cell-actions-${interaction.id}`}>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  data-testid={`button-actions-${interaction.id}`}
                                >
                                  <MoreVertical className="h-4 w-4" />
                                  <span className="sr-only">Actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-52">
                                <DropdownMenuLabel>Interaction</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleOpenDrawer(interaction)}
                                  data-testid={`button-view-interaction-${interaction.id}`}
                                >
                                  <Eye className="h-4 w-4 mr-2" />
                                  View Details
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <HighlightInteractionButton
                                  variant="menu-item"
                                  interactionId={interaction.id}
                                  isHighlighted={
                                    interaction.flagReasons?.some(
                                      (r) => r.type === "highlighted"
                                    ) ?? false
                                  }
                                  animalId={interaction.animalId}
                                />
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Animal Profile</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => {
                                    setPreviewAnimalId(interaction.animalId);
                                    setAnimalPreviewOpen(true);
                                  }}
                                  data-testid={`button-view-animal-preview-${interaction.id}`}
                                >
                                  <PawPrint className="h-4 w-4 mr-2" />
                                  View Preview
                                </DropdownMenuItem>
                                <DropdownMenuItem asChild>
                                  <Link
                                    href={`/animals/${encodeURIComponent(interaction.animalId)}`}
                                    data-testid={`link-view-animal-full-${interaction.id}`}
                                  >
                                    <ExternalLink className="h-4 w-4 mr-2" />
                                    View Full Profile
                                  </Link>
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuLabel>Tasks / Meetings</DropdownMenuLabel>
                                <DropdownMenuItem
                                  onClick={() => handleOpenLinker(interaction)}
                                  data-testid={`button-add-to-${interaction.id}`}
                                >
                                  <LinkIcon className="h-4 w-4 mr-2" />
                                  Add to...
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </td>
                        </tr>
                          {excerpt && (
                            <tr
                              className="border-b cursor-pointer hover:bg-muted/50 transition-colors"
                              onClick={() => handleOpenDrawer(interaction)}
                              data-testid={`row-excerpt-${interaction.id}`}
                            >
                              <td
                                colSpan={10}
                                className="px-3 py-1.5 text-xs text-muted-foreground font-mono leading-relaxed border-l-2 border-l-amber-300 dark:border-l-amber-600"
                              >
                                {highlightSubstringMatches(excerpt, searchQuery.trim())}
                              </td>
                            </tr>
                          )}
                          </Fragment>
                        );
                      })
                    )}
                  </tbody>
            </table>
          </div>

          {displayPagination.totalPages > 0 && (
            <div className="flex items-center justify-between px-4 py-3">
              <div className="text-sm text-muted-foreground">
                Showing {((displayPagination.page - 1) * displayPagination.limit) + 1} to {Math.min(displayPagination.page * displayPagination.limit, displayPagination.total)} of {displayPagination.total} interactions
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.max(1, displayPagination.page - 1))}
                  disabled={displayPagination.page <= 1}
                  data-testid="button-prev-page"
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Prev
                </Button>
                <span className="text-sm" data-testid="text-page-info">
                  Page {displayPagination.page} of {displayPagination.totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(Math.min(displayPagination.totalPages, displayPagination.page + 1))}
                  disabled={displayPagination.page >= displayPagination.totalPages}
                  data-testid="button-next-page"
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </>
      )}

      {selectedInteraction && (
        <InteractionDetailDrawer
          open={drawerOpen}
          onOpenChange={setDrawerOpen}
          interaction={selectedInteraction}
          animalId={selectedInteraction.animalId}
          onInteractionChange={setSelectedInteraction}
        />
      )}

      {linkerInteraction && (
        <EntityLinkerDialog
          open={entityLinkerOpen}
          onOpenChange={setEntityLinkerOpen}
          entityType="interaction"
          entityId={linkerInteraction.id}
          entityLabel={`${linkerInteraction.eventType || "Interaction"} — ${linkerInteraction.occurredAt ? new Date(linkerInteraction.occurredAt).toLocaleDateString() : ""}`.trim()}
          animalId={linkerInteraction.animalId}
        />
      )}

      {previewAnimalId && (
        <AnimalDetailDrawer
          open={animalPreviewOpen}
          onOpenChange={setAnimalPreviewOpen}
          animalId={previewAnimalId}
          context="interactions"
        />
      )}
    </div>
  );
}
